import { useEffect, useState } from 'react';
import { Button, Input, Modal } from '@react-apps/design-system';
import type { Subject } from '../../domain/entities/content.entity';
import { useCreateSubject, useRenameSubject } from '../hooks/use-subjects';
import { getApiErrorMessage } from '../display';

interface SubjectFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Quando presente, o modal renomeia; senão, cria. */
  subject?: Subject | null;
}

/** Cria (sem `subject`) ou renomeia (com `subject`) um assunto. */
export function SubjectFormModal({
  open,
  onClose,
  subject,
}: SubjectFormModalProps) {
  const isRename = !!subject;
  const [name, setName] = useState('');
  const create = useCreateSubject();
  const rename = useRenameSubject();
  const mutation = isRename ? rename : create;

  useEffect(() => {
    if (open) setName(subject?.name ?? '');
  }, [open, subject]);

  const close = () => {
    mutation.reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (isRename) {
      rename.mutate(
        { subjectId: subject.id, input: { name: trimmed } },
        { onSuccess: close },
      );
    } else {
      create.mutate({ name: trimmed }, { onSuccess: close });
    }
  };

  const canSubmit =
    name.trim().length > 0 &&
    name.trim() !== (subject?.name ?? '') &&
    !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={close}
      title={isRename ? 'Renomear assunto' : 'Criar assunto'}
      description={
        isRename
          ? 'Atualize o nome do assunto.'
          : 'Um assunto é o nó da árvore de classificação de conteúdo.'
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nome"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ex.: Cardiologia"
          required
          autoFocus
        />
        {mutation.error && (
          <p className="text-sm text-red-600">
            {getApiErrorMessage(
              mutation.error,
              isRename ? 'Falha ao renomear' : 'Falha ao criar assunto',
            )}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {mutation.isPending ? 'Salvando…' : isRename ? 'Salvar' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
